/**
 * Extension of a file name, without the dot and with its original casing kept, so that a name
 * rebuilt out of it stays the same. Returns an empty string when the name carries no extension
 * (`report`, `.`, `2&&&`).
 *
 * @param displayName - full file name as it is stored and displayed
 */
export function getFileExtension(displayName?: string | null): string {
  const name = (displayName ?? '').trim();
  const separatorIndex = name.lastIndexOf('.');

  if (separatorIndex < 1 || separatorIndex === name.length - 1) {
    return '';
  }

  return name.slice(separatorIndex + 1);
}

/**
 * File name without its extension. Falls back to the full name when stripping the
 * extension would leave nothing behind, so a name is never rendered empty.
 *
 * @param displayName - full file name as it is stored and displayed
 */
export function getFileBaseName(displayName?: string | null): string {
  const name = (displayName ?? '').trim();
  const extension = getFileExtension(name);

  if (!extension) {
    return name;
  }

  const baseName = name.slice(0, name.length - extension.length - 1);

  return baseName || name;
}

/**
 * Joins a base name and an extension back into a file name.
 *
 * @param baseName - file name without the extension
 * @param extension - extension without the dot, may be empty
 */
export function buildFileDisplayName(baseName: string, extension?: string | null): string {
  const name = (baseName ?? '').trim();

  if (!extension) {
    return name;
  }

  return `${name}.${extension}`;
}
